import * as THREE from 'three';
import { DECAL_CELLS } from '../gen/mesh/decals';

/**
 * The road decal atlas (4 × 4 cells of 256², layout DECAL_CELLS in src/gen/mesh/decals.ts), drawn
 * procedurally: worn white and yellow paint, a manhole, a drain grate, an oil stain, cracks, an asphalt
 * patch. Deterministic (a fixed-seed noise), so it looks the same in every session.
 */
function decalAtlas(): THREE.CanvasTexture {
  const S = 256;
  const c = document.createElement('canvas');
  c.width = c.height = S * 4;
  const g = c.getContext('2d')!;
  let seed = 12345;
  const rnd = () => ((seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  const cell = (i: number) => [(i % 4) * S, Math.floor(i / 4) * S] as const;
  const paint = (i: number, rgb: string) => {
    const [x, y] = cell(i);
    g.fillStyle = rgb;
    g.fillRect(x, y, S, S);
    // Wear: speckles of missing paint.
    g.globalCompositeOperation = 'destination-out';
    for (let k = 0; k < 900; k++) {
      g.fillStyle = `rgba(0,0,0,${0.3 + rnd() * 0.6})`;
      const r = 1 + rnd() * 5;
      g.beginPath();
      g.arc(x + rnd() * S, y + rnd() * S, r, 0, Math.PI * 2);
      g.fill();
    }
    g.globalCompositeOperation = 'source-over';
  };
  paint(DECAL_CELLS.white, 'rgba(236,234,226,0.95)');
  paint(DECAL_CELLS.yellow, 'rgba(226,176,40,0.95)');
  {
    const [x, y] = cell(DECAL_CELLS.manhole);
    const cx = x + S / 2;
    const cy = y + S / 2;
    g.fillStyle = '#2e2d2b';
    g.beginPath();
    g.arc(cx, cy, S * 0.46, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = '#4a4844';
    g.lineWidth = 10;
    g.beginPath();
    g.arc(cx, cy, S * 0.4, 0, Math.PI * 2);
    g.stroke();
    g.lineWidth = 6;
    for (let k = -3; k <= 3; k++) {
      g.beginPath();
      g.moveTo(cx - S * 0.3, cy + k * 22);
      g.lineTo(cx + S * 0.3, cy + k * 22);
      g.stroke();
    }
  }
  {
    const [x, y] = cell(DECAL_CELLS.drain);
    g.fillStyle = '#3a3936';
    g.fillRect(x + 8, y + 8, S - 16, S - 16);
    g.fillStyle = '#0d0d0d';
    for (let k = 0; k < 9; k++) g.fillRect(x + 24 + k * 24, y + 24, 12, S - 48);
  }
  {
    const [x, y] = cell(DECAL_CELLS.stain);
    for (let k = 0; k < 40; k++) {
      const r = S * (0.08 + rnd() * 0.22);
      const px = x + S / 2 + (rnd() - 0.5) * S * 0.4;
      const py = y + S / 2 + (rnd() - 0.5) * S * 0.4;
      const grad = g.createRadialGradient(px, py, 0, px, py, r);
      grad.addColorStop(0, 'rgba(20,18,16,0.22)');
      grad.addColorStop(1, 'rgba(20,18,16,0)');
      g.fillStyle = grad;
      g.fillRect(x, y, S, S);
    }
  }
  {
    const [x, y] = cell(DECAL_CELLS.crack);
    g.strokeStyle = 'rgba(15,15,14,0.8)';
    for (let k = 0; k < 5; k++) {
      g.lineWidth = 1 + rnd() * 2.5;
      g.beginPath();
      let px = x + 10;
      let py = y + S * (0.2 + rnd() * 0.6);
      g.moveTo(px, py);
      while (px < x + S - 10) {
        px += 8 + rnd() * 18;
        py = Math.min(y + S - 5, Math.max(y + 5, py + (rnd() - 0.5) * 26));
        g.lineTo(px, py);
      }
      g.stroke();
    }
  }
  {
    const [x, y] = cell(DECAL_CELLS.patch);
    g.fillStyle = 'rgba(28,28,30,0.55)';
    g.fillRect(x + 10, y + 10, S - 20, S - 20);
    g.strokeStyle = 'rgba(10,10,10,0.6)';
    g.lineWidth = 3;
    g.strokeRect(x + 10, y + 10, S - 20, S - 20);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/** Decals: alpha-blended over the road, no depth writes, pulled forward by a polygon offset. */
export function decalMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    map: decalAtlas(),
    transparent: true,
    depthWrite: false,
    roughness: 0.8,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -8,
  });
}
